// Ticker Debug Info Component - Shows Yahoo Finance ticker mappings
import React, { useState, useEffect } from 'react';
import { ExternalLink, Copy, CheckCircle, AlertCircle } from 'lucide-react';
import { YAHOO_SYMBOL_MAPPINGS } from '../services/yahooFinanceService';
import { toast } from 'sonner';

interface TickerDebugInfoProps {
  isVisible?: boolean;
  onClose?: () => void;
}

const TickerDebugInfo: React.FC<TickerDebugInfoProps> = ({ 
  isVisible = false, 
  onClose 
}) => {
  const [copiedTicker, setCopiedTicker] = useState<string | null>(null);

  // Copy ticker to clipboard
  const copyTicker = async (ticker: string) => {
    try {
      await navigator.clipboard.writeText(ticker);
      setCopiedTicker(ticker);
      toast.success(`Ticker ${ticker} copiato negli appunti`);
      setTimeout(() => setCopiedTicker(null), 2000);
    } catch (error) {
      toast.error('Errore durante la copia');
    }
  };

  // Open Yahoo Finance page for ticker
  const openYahooFinance = (ticker: string) => {
    const url = `https://finance.yahoo.com/quote/${ticker}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Get category color
  const getCategoryColor = (symbol: string) => {
    if (symbol.includes('USD') || symbol.includes('EUR') || symbol.includes('GBP') || symbol.includes('JPY') || symbol.includes('CHF') || symbol.includes('CAD')) {
      return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    }
    if (symbol.includes('GOLD') || symbol.includes('SILVER') || symbol.includes('COPPER') || symbol.includes('PLATINUM')) {
      return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
    }
    if (symbol.includes('CRUDE') || symbol.includes('GASOLINE') || symbol.includes('NATGAS')) {
      return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
    }
    if (symbol.includes('SP500') || symbol.includes('NASDAQ') || symbol.includes('DOW') || symbol.includes('VIX') || symbol.includes('DXY')) {
      return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    }
    return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
  };

  // Get category name
  const getCategoryName = (symbol: string) => {
    if (symbol.includes('USD') || symbol.includes('EUR') || symbol.includes('GBP') || symbol.includes('JPY') || symbol.includes('CHF') || symbol.includes('CAD')) {
      return 'Valute';
    }
    if (symbol.includes('GOLD') || symbol.includes('SILVER') || symbol.includes('COPPER') || symbol.includes('PLATINUM')) {
      return 'Metalli Preziosi';
    }
    if (symbol.includes('CRUDE') || symbol.includes('GASOLINE') || symbol.includes('NATGAS')) {
      return 'Energia';
    }
    if (symbol.includes('SP500') || symbol.includes('NASDAQ') || symbol.includes('DOW') || symbol.includes('VIX') || symbol.includes('DXY')) {
      return 'Indici';
    }
    return 'Altri';
  };

  // Group symbols by category
  const groupedSymbols = Object.entries(YAHOO_SYMBOL_MAPPINGS).reduce((acc, [symbol, mapping]) => {
    const category = getCategoryName(symbol);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push({ symbol, ticker: mapping.ticker, name: mapping.name });
    return acc;
  }, {} as Record<string, Array<{ symbol: string; ticker: string; name: string }>>);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white">Mappatura Ticker Yahoo Finance</h2>
            <p className="text-sm text-gray-400 mt-1">
              Verifica i ticker utilizzati per ottenere i dati da Yahoo Finance
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
          <div className="space-y-6">
            {Object.entries(groupedSymbols).map(([category, symbols]) => (
              <div key={category} className="space-y-3">
                <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
                  {category} ({symbols.length})
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {symbols.map(({ symbol, ticker, name }) => (
                    <div 
                      key={symbol} 
                      className={`p-4 rounded-lg border transition-all hover:scale-105 ${getCategoryColor(symbol)}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-semibold text-sm">{symbol}</span>
                          <div className="text-xs text-gray-400">{name}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => copyTicker(ticker)}
                            className="p-1 hover:bg-white/10 rounded transition-colors"
                            title="Copia ticker"
                          >
                            {copiedTicker === ticker ? (
                              <CheckCircle className="w-3 h-3 text-green-400" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </button>
                          <button
                            onClick={() => openYahooFinance(ticker)}
                            className="p-1 hover:bg-white/10 rounded transition-colors"
                            title="Apri su Yahoo Finance"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="font-mono text-xs bg-black/20 px-2 py-1 rounded">
                        {ticker}
                      </div>
                      
                      <div className="mt-2 text-xs opacity-75">
                        Clicca per verificare su Yahoo Finance
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Instructions */}
          <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-300 mb-2">Come verificare i dati:</h4>
                <ul className="text-sm text-blue-200 space-y-1">
                  <li>• Clicca sull'icona <ExternalLink className="w-3 h-3 inline" /> per aprire il ticker su Yahoo Finance</li>
                  <li>• Confronta il prezzo mostrato su Yahoo Finance con quello nell'app</li>
                  <li>• Se i prezzi non corrispondono, il ticker potrebbe essere errato</li>
                  <li>• Usa l'icona <Copy className="w-3 h-3 inline" /> per copiare il ticker negli appunti</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TickerDebugInfo;